import { Login } from '@platform/client';
import { Button } from '@platform/components';
import { useEffect, useState } from 'react';
import { MovingWorldsBannerLogoLink } from '../../layout/MovingWorldsLogo';

export const PasswordResetEmailVerificationPage = () => {
  const [email, setEmail] = useState('');

  useEffect(() => {
    //Done in useEffect due to ssr
    //Regex replaces spaces with + to undo URLSearchParams default interpreting of them as spaces

    const urlParams = new URLSearchParams(window.location.search);
    let emailParam = urlParams.get('email');
    if (emailParam) {
      if (emailParam.endsWith('/')) {
        emailParam = emailParam.slice(0, -1);
      }
      const decodedEmail = decodeURIComponent(emailParam);
      setEmail(decodedEmail.replace(/\s/g, '+'));
    }
  }, []);

  const resendMutation = Login.useResendResetEmail();

  const resendLoginLink = () => {
    resendMutation.mutate({
      data: {
        email,
      },
    });
  };

  return (
    <div className='w-full h-full flex flex-col gap-8 md:justify-between p-4 overflow-y-scroll'>
      <div className='text-brand-500 w-full md:w-[224px] h-5'>
        <MovingWorldsBannerLogoLink className='flex justify-center items-center h-16 md:h-5 min-h-16 md:min-h-5' />
      </div>
      <div className='flex flex-col gap-10 items-center text-center pt-8 md:pt-0'>
        <h2 className='text-display-sm md:text-display-xl'>
          Almost there! Check your <span className='text-brand-500'>email.</span>
        </h2>
        <div className='w-full md:w-[560px] text-display-xs'>
          We just sent a login link to <span className='font-bold break-words'>{email}</span>. Use
          this link to confirm your account.
        </div>
        <div className='flex flex-col md:flex-row gap-4 items-center justify-center'>
          <OpenInGmailButton email={email} />
          <OpenInOutlookButton email={email} />
        </div>
        <div className='w-full md:w-[560px] text-display-xs'>
          Couldn't find it? Make sure to check your spam! You can also{' '}
          <span className='text-brand-500 underline cursor-pointer' onClick={resendLoginLink}>
            resend login link
          </span>{' '}
          or{' '}
          <a className='text-brand-500 underline' href='mailto:match@movingworlds.org'>
            email us for help
          </a>
        </div>
      </div>
      <div /> {/* Empty div justify in thirds */}
    </div>
  );
};

const GmailIcon = () => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='25' height='24' viewBox='0 0 25 24' fill='none'>
      <g clipPath='url(#clip0_1516_33349)'>
        <path
          d='M2.13636 21.0021H5.95456V11.7294L3.45434 7.36353L0.5 7.63845V19.3657C0.5 20.2698 1.23228 21.0021 2.13636 21.0021Z'
          fill='#0085F7'
        />
        <path
          d='M19.0454 21.0021H22.8636C23.7677 21.0021 24.5 20.2698 24.5 19.3657V7.63845L21.5499 7.36353L19.0455 11.7294V21.0021H19.0454Z'
          fill='#00A94B'
        />
        <path
          d='M19.0456 4.63856L16.8027 8.91839L19.0456 11.7295L24.5001 7.63856V5.45676C24.5001 3.43448 22.1915 2.27948 20.5728 3.49312L19.0456 4.63856Z'
          fill='#FFBC00'
        />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M5.95455 11.7296L3.81738 7.21506L5.95455 4.63867L12.5 9.54775L19.0454 4.63867V11.7296L12.5 16.6387L5.95455 11.7296Z'
          fill='#FF4131'
        />
        <path
          d='M0.5 5.45676V7.63856L5.95456 11.7295V4.63856L4.42728 3.49312C2.80864 2.27948 0.5 3.43448 0.5 5.45676Z'
          fill='#E51C19'
        />
      </g>
      <defs>
        <clipPath id='clip0_1516_33349'>
          <rect width='24' height='24' fill='white' transform='translate(0.5)' />
        </clipPath>
      </defs>
    </svg>
  );
};

const OutlookIcon = () => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='25' height='24' viewBox='0 0 25 24' fill='none'>
      <g clipPath='url(#clip0_1516_33358)'>
        <path
          d='M24.5002 12.6C24.5015 12.3983 24.4045 12.2105 24.2446 12.105H24.2418L24.2317 12.099L15.9154 6.80701C15.8795 6.78093 15.8423 6.75709 15.8038 6.73561C15.4827 6.55753 15.1013 6.55753 14.7802 6.73561C14.7417 6.7571 14.7045 6.78093 14.6685 6.80701L6.3523 12.099L6.34226 12.105C6.08809 12.2749 6.01017 12.6342 6.16822 12.9074C6.21479 12.9879 6.27894 13.055 6.35509 13.1028L14.6714 18.3948C14.7074 18.4207 14.7447 18.4445 14.783 18.4662C15.1041 18.6443 15.4855 18.6443 15.8066 18.4662C15.845 18.4445 15.8822 18.4207 15.9183 18.3948L24.2345 13.1028C24.4007 12.9987 24.5021 12.8067 24.5002 12.6Z'
          fill='#0A2767'
        />
        <path
          d='M7.32144 9.0666H12.7789V14.4444H7.32144V9.0666ZM23.3836 3.6V1.13999C23.3967 0.524937 22.9438 0.0147381 22.3717 0H8.20945C7.63731 0.0147381 7.18444 0.524937 7.19754 1.13999V3.6L15.5696 5.99999L23.3836 3.6Z'
          fill='#0364B8'
        />
        <path d='M7.19775 3.6001H12.7791V9.00009H7.19775V3.6001Z' fill='#0078D4' />
        <path
          d='M18.3607 3.6001H12.7793V9.00009L18.3607 14.4001H23.3839V9.00009L18.3607 3.6001Z'
          fill='#28A8EA'
        />
        <path d='M12.7793 9H18.3607V14.4H12.7793V9Z' fill='#0078D4' />
        <path d='M12.7793 14.3999H18.3607V19.7999H12.7793V14.3999Z' fill='#0364B8' />
        <path d='M7.32178 14.4443H12.7793V19.3331H7.32178V14.4443Z' fill='#14447D' />
        <path d='M18.3604 14.3999H23.3836V19.7999H18.3604V14.3999Z' fill='#0078D4' />
        <path
          d='M24.2445 13.0699L24.2339 13.0759L15.9176 18.1039C15.8813 18.1279 15.8445 18.1507 15.806 18.1711C15.6647 18.2434 15.5113 18.2842 15.355 18.2911L14.9007 18.0055C14.8623 17.9848 14.825 17.9617 14.7891 17.9365L6.36117 12.7657H6.35726L6.08154 12.6001V22.7785C6.08584 23.4576 6.60127 24.0044 7.23298 24.0001H23.3665C23.376 24.0001 23.3844 23.9953 23.3945 23.9953C23.5279 23.9861 23.6594 23.9566 23.7851 23.9077C23.8395 23.883 23.8919 23.8537 23.942 23.8201C23.9794 23.7973 24.0436 23.7475 24.0436 23.7475C24.3295 23.5201 24.4987 23.1609 24.5001 22.7785V12.6001C24.4999 12.7948 24.4023 12.9743 24.2445 13.0699Z'
          fill='url(#paint0_linear_1516_33358)'
        />
        <path
          opacity='0.5'
          d='M24.0537 12.5597V13.1837L15.3579 19.6199L6.35506 12.7697C6.35506 12.7664 6.35256 12.7637 6.34948 12.7637L5.52344 12.2297V11.7797L5.8639 11.7737L6.5839 12.2177L6.60063 12.2237L6.66203 12.2657C6.66203 12.2657 15.1234 17.4557 15.1457 17.4677L15.4695 17.6717C15.4974 17.6597 15.5253 17.6477 15.5588 17.6357C15.5755 17.6237 23.9587 12.5537 23.9587 12.5537L24.0537 12.5597Z'
          fill='#0A2767'
        />
        <path
          d='M24.2445 13.0699L24.2339 13.0765L15.9176 18.1045C15.8813 18.1285 15.8445 18.1513 15.806 18.1717C15.483 18.3413 15.1053 18.3413 14.7824 18.1717C14.7441 18.1513 14.7069 18.1289 14.6707 18.1045L6.35447 13.0765L6.34443 13.0699C6.18387 12.9763 6.08321 12.7964 6.08154 12.6001V22.7785C6.08554 23.4575 6.60079 24.0044 7.23238 24.0001C7.23238 24.0001 7.2324 24.0001 7.23243 24.0001H23.3492C23.9808 24.0044 24.4961 23.4575 24.5001 22.7785C24.5001 22.7785 24.5001 22.7785 24.5001 22.7785V12.6001C24.4999 12.7948 24.4022 12.9743 24.2445 13.0699Z'
          fill='#1490DF'
        />
        <path
          opacity='0.1'
          d='M16.0386 18.03L15.9141 18.105C15.878 18.1297 15.8408 18.1524 15.8025 18.1728C15.6653 18.2452 15.5162 18.2876 15.3638 18.2976L18.5279 22.32L24.0473 23.7498C24.1985 23.627 24.3188 23.4655 24.3973 23.28L16.0386 18.03Z'
          fill='black'
        />
        <path
          opacity='0.05'
          d='M16.6023 17.6892L15.9141 18.105C15.878 18.1297 15.8408 18.1523 15.8025 18.1728C15.6653 18.2452 15.5162 18.2876 15.3638 18.2976L16.8462 22.6914L24.049 23.748C24.3327 23.519 24.4998 23.1598 24.4999 22.7784V22.647L16.6023 17.6892Z'
          fill='black'
        />
        <path
          d='M7.24805 24.0001H23.3476C23.5953 24.0014 23.8369 23.9173 24.0369 23.7601L14.9001 18.0067C14.8617 17.986 14.8245 17.963 14.7885 17.9377L6.3606 12.7669H6.3567L6.08154 12.6001V22.7437C6.08091 23.4369 6.60318 23.9994 7.24805 24.0001Z'
          fill='#28A8EA'
        />
        <path
          opacity='0.1'
          d='M13.8955 5.89984V18.6978C13.8945 19.1466 13.6407 19.5499 13.2536 19.7178C13.1337 19.7732 13.0046 19.8018 12.8741 19.8018H6.08154V5.40004H7.19783V4.80005H12.8741C13.438 4.80235 13.8943 5.29369 13.8955 5.89984Z'
          fill='black'
        />
        <path
          opacity='0.2'
          d='M13.3374 6.49975V19.2977C13.3388 19.4427 13.3102 19.5861 13.2536 19.7177C13.0986 20.1284 12.7283 20.3979 12.316 20.3999H6.08154V5.39995H12.316C12.4779 5.39821 12.6375 5.44163 12.7792 5.52596C13.1214 5.71126 13.3372 6.08791 13.3374 6.49975Z'
          fill='black'
        />
        <path
          opacity='0.2'
          d='M13.3374 6.49975V18.0977C13.3346 18.7036 12.8796 19.1947 12.316 19.1999H6.08154V5.39995H12.316C12.4779 5.39821 12.6375 5.44163 12.7792 5.52596C13.1214 5.71126 13.3372 6.08791 13.3374 6.49975Z'
          fill='black'
        />
        <path
          opacity='0.2'
          d='M12.7792 6.4997V18.0977C12.7786 18.7045 12.3223 19.1969 11.7578 19.1999H6.08154V5.3999H11.7578C12.3222 5.40023 12.7795 5.89236 12.7792 6.49911C12.7792 6.4993 12.7792 6.4995 12.7792 6.4997Z'
          fill='black'
        />
        <path
          d='M1.52307 5.3999H11.756C12.321 5.3999 12.7791 5.89231 12.7791 6.4997V17.5001C12.7791 18.1075 12.321 18.5999 11.756 18.5999H1.52307C0.958037 18.5999 0.5 18.1075 0.5 17.5001V6.4997C0.5 5.89231 0.95805 5.3999 1.52307 5.3999Z'
          fill='url(#paint1_linear_1516_33358)'
        />
        <path
          d='M3.69779 10.0128C3.94994 9.43529 4.35694 8.95199 4.86654 8.62502C5.43089 8.27769 6.07345 8.10454 6.72347 8.12461C7.32594 8.11057 7.92042 8.27474 8.44198 8.59921C8.93237 8.91358 9.32732 9.37422 9.57891 9.9252C9.8529 10.5323 9.98945 11.2005 9.97743 11.8752C9.9907 12.5803 9.85021 13.279 9.56719 13.9152C9.30961 14.4859 8.90213 14.9629 8.3951 15.2874C7.85343 15.6218 7.23687 15.7903 6.61241 15.7746C5.99707 15.7906 5.38948 15.6245 4.85594 15.2946C4.36131 14.9798 3.9614 14.5187 3.70394 13.9662C3.42833 13.3678 3.29015 12.7072 3.30097 12.0396C3.28948 11.3405 3.42514 10.6476 3.69779 10.0128ZM4.94356 13.2708C5.078 13.6359 5.30598 13.9526 5.60104 14.184C5.90159 14.4098 6.26147 14.5263 6.62802 14.5164C7.01838 14.533 7.40316 14.4125 7.72421 14.1732C8.01553 13.9425 8.23753 13.625 8.36271 13.26C8.50266 12.8524 8.57172 12.4206 8.56643 11.9862C8.57076 11.5476 8.50584 11.1114 8.37443 10.6962C8.25837 10.3213 8.04353 9.99074 7.75602 9.74461C7.44302 9.49395 7.05927 9.36682 6.66932 9.38461C6.29482 9.37419 5.92688 9.4916 5.61835 9.72001C5.31829 9.95244 5.08598 10.2719 4.94858 10.641C4.64378 11.4871 4.64219 12.4247 4.94411 13.272L4.94356 13.2708Z'
          fill='white'
        />
        <path d='M18.3604 3.6001H23.3836V9.00009H18.3604V3.6001Z' fill='#50D9FF' />
      </g>
      <defs>
        <linearGradient
          id='paint0_linear_1516_33358'
          x1='15.2908'
          y1='12.6001'
          x2='15.2908'
          y2='24.0001'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#35B8F1' />
          <stop offset='1' stopColor='#28A8EA' />
        </linearGradient>
        <linearGradient
          id='paint1_linear_1516_33358'
          x1='2.63312'
          y1='4.54054'
          x2='11.5461'
          y2='18.9005'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#1784D9' />
          <stop offset='0.5' stopColor='#107AD5' />
          <stop offset='1' stopColor='#0A63C9' />
        </linearGradient>
        <clipPath id='clip0_1516_33358'>
          <rect width='24' height='24' fill='white' transform='translate(0.5)' />
        </clipPath>
      </defs>
    </svg>
  );
};

type OpenInOutlookButtonProps = {
  email: string;
};

const OpenInOutlookButton = (props: OpenInOutlookButtonProps) => {
  const { email } = props;
  const sniperLink = `https://outlook.live.com/mail/?login_hint=${encodeURIComponent(email)}`;
  return (
    <Button target='_blank' href={sniperLink} variant='secondary-gray' size='xl'>
      <OutlookIcon /> <span>See in Outlook</span>
    </Button>
  );
};

type OpenInGmailButtonProps = {
  email: string;
};
const OpenInGmailButton = (props: OpenInGmailButtonProps) => {
  const { email } = props;
  const sniperLink = `https://mail.google.com/mail/u/${email}/#search/from%3A(transform%40movingworlds.org)+in%3Aanywhere+newer_than%3A1h`;
  return (
    <Button target='_blank' href={sniperLink} variant='secondary-gray' size='xl'>
      <GmailIcon /> {'See in Gmail'}
    </Button>
  );
};
